import {makeAutoObservable} from "mobx";
import {checkDatesEqual} from "../../utils/dateUtils";
import {utcToZonedTime, zonedTimeToUtc} from "date-fns-tz";
import {fifteenMinutesInMilliseconds} from "../../shared/constants";

export class DateObservable {
  date: Date | null;
  minDate: Date | null;
  validate?: (toValidate: Date | null) => boolean;
  isDirty: boolean;
  initialValue: number;
  maxDate: Date | null;
  constructor(
    date?: Date | null,
    minDate?: Date | null,
    validate?: (toValidate: Date | null) => boolean,
    maxDate?: Date | null
  ) {
    makeAutoObservable(this);
    this.date = date ?? null;
    this.initialValue = date?.getTime() ?? 0;
    this.minDate = minDate ?? null;
    this.validate = validate;
    this.isDirty = false;
    this.maxDate = maxDate ?? null;
  }

  setIsDirty = (value: boolean) => {
    this.isDirty = value;
  };

  get isValid() {
    return (
      (this.validate === undefined ||
        !this.isDirty ||
        this.validate(this.date)) &&
      this.isTimeValid
    );
  }

  get isTimeValid() {
    if (this.isDirty && this.date) {
      return (
        this.date >= (this.minDate || this.date) &&
        this.date <= (this.maxDate || this.date)
      );
    }
    return true;
  }

  setDate = (value: Date | null) => {
    if (this.initialValue === 0 && !!value) this.initialValue = value.getTime();
    this.date = value;
    this.setIsDirty(true);
  };

  setMinDate = (value: Date | null) => {
    this.minDate = value;
    this.minDate?.setMilliseconds(0);
  };

  calculateMinDate = (timeZone: string): Date => {
    let date = new Date(new Date().setHours(0, 0, 0, 0));
    if (this.minDate) {
      date = zonedTimeToUtc(
        new Date(
          utcToZonedTime(
            new Date().setHours(
              this.minDate?.getHours(),
              this.minDate?.getMinutes()
            ),
            timeZone
          )
        ),
        timeZone
      );
      if (this.date && !checkDatesEqual(this.date, this.minDate)) {
        date = zonedTimeToUtc(
          new Date(utcToZonedTime(new Date().setHours(0, 0, 0, 0), timeZone)),
          timeZone
        );
      }
    }
    // round date up to next 15 minute interval
    return new Date(
      Math.ceil(date.getTime() / fifteenMinutesInMilliseconds) *
        fifteenMinutesInMilliseconds
    );
  };

  get hasChange(): boolean {
    return this.date?.getTime() !== this.initialValue;
  }
}
